'use client';

import {
  Autocomplete,
  Box,
  Divider,
  FormControl,
  FormLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type { AzureCmkEligibleApp } from '@repo/api-gw-sdk';
import { Provider } from '@repo/api-gw-sdk';
import { useEffect, useMemo, useState } from 'react';

import { useDAL } from '@/contexts/useDAL';
import type { BackupVault } from '@/data/vaults/backupVault';

import { BottomDialog } from '../dialogs/bottomDialog';
import { FormTextField } from '../form/formTextField';
import { getCloudFieldConfig } from '../form/validation/cloudFieldValidation';
import { PanelWrapper } from '../panels/panelWrapper';
import { BrowserGrid } from '../shared/browserGrid';
import { CopyableCode } from '../shared/copyableCode';
import { ChangeableTextField } from '../text/changeableTextField';

const keyVaultKeyUriConfig = getCloudFieldConfig(
  Provider.Azure,
  'keyVaultKeyUri'
);

function tenantIdListed(
  tenantId: string | undefined,
  onboardedTenantIds: readonly string[]
): boolean {
  if (!tenantId?.trim()) {
    return false;
  }
  const t = tenantId.trim().toLowerCase();
  return onboardedTenantIds.some((id) => id.toLowerCase() === t);
}

const EMPTY_TENANT_IDS: string[] = [];

const AzureKeyUriDescription = () => (
  <Typography variant='body1' component='span'>
    Choose the Microsoft Entra tenant where the Key Vault lives, the Eon app
    that will access the key (service principal in that tenant), and the Key
    Vault key URI. The storage account&apos;s managed identity must be granted
    access to the key.
  </Typography>
);

const AzureKeyVaultKeyUriEditorPanel = ({
  open,
  onClose,
  data,
  setData,
  vaultAccountId,
  defaultAzureTenantId,
}: {
  open: boolean;
  onClose: () => void;
  data: BackupVault;
  setData: (data: BackupVault) => void;
  vaultAccountId: string;
  defaultAzureTenantId?: string;
}) => {
  const dal = useDAL();
  const [localData, setLocalData] = useState(data);

  const cmkTenantId =
    localData.vaultAttributes.azure?.cmkKeyVaultTenantId?.trim() || '';

  const { data: tenants, isLoading: loadingTenants } =
    dal.cloudAccounts.azureApps.useListTenants();
  const { data: eligibleApps, isLoading: loadingApps } =
    dal.vaults.useListAzureCmkEligibleApps(vaultAccountId, cmkTenantId);

  const onboardedTenantIds = tenants?.tenantIds ?? EMPTY_TENANT_IDS;

  useEffect(() => {
    if (!open || loadingTenants) {
      return;
    }
    const fromForm =
      data.vaultAttributes.azure?.cmkKeyVaultTenantId?.trim() ?? '';
    const defaultIfListed =
      defaultAzureTenantId &&
      tenantIdListed(defaultAzureTenantId, onboardedTenantIds)
        ? defaultAzureTenantId.trim()
        : '';
    setLocalData((prev) => {
      const newTenant = fromForm || defaultIfListed;
      const tenantChanged =
        newTenant.toLowerCase() !==
        (
          prev.vaultAttributes.azure?.cmkKeyVaultTenantId?.trim() || ''
        ).toLowerCase();
      return {
        ...prev,
        vaultAttributes: {
          ...prev.vaultAttributes,
          azure: {
            ...prev.vaultAttributes.azure,
            cmkKeyVaultTenantId: newTenant,
            ...(tenantChanged ? { cmkAppRegistrationId: '' } : {}),
          },
        },
      };
    });
  }, [open, data, defaultAzureTenantId, loadingTenants, onboardedTenantIds]);

  const tenantOptions = useMemo(() => {
    const base = [...onboardedTenantIds];
    if (
      cmkTenantId &&
      !base.some((id) => id.toLowerCase() === cmkTenantId.toLowerCase())
    ) {
      base.push(cmkTenantId);
    }
    return base;
  }, [onboardedTenantIds, cmkTenantId]);
  const appOptions = eligibleApps?.apps ?? [];

  const selectedApp: AzureCmkEligibleApp | null =
    appOptions.find(
      (a) =>
        a.appRegistrationId ===
        localData.vaultAttributes.azure?.cmkAppRegistrationId
    ) ?? null;

  const keyUri = localData.vaultAttributes.azure?.encryptionKey?.trim() ?? '';
  const keyVaultName = /^https:\/\/([a-z0-9-]+)\.vault\.azure\.net\//i.exec(
    keyUri
  )?.[1];

  const spId = selectedApp?.servicePrincipalId ?? '';
  const canShowRoleScript = !!(cmkTenantId && spId && keyVaultName);

  const roleAssignmentScript = `\
# Log in to the tenant that owns the Key Vault
az login --tenant "${cmkTenantId || '<tenant-id>'}"

# Assign "Key Vault Crypto Service Encryption User" to the Eon app's service principal
az role assignment create \\
  --assignee-object-id "${spId || '<service-principal-id>'}" \\
  --assignee-principal-type ServicePrincipal \\
  --role "e147488a-f6f5-4113-8e2d-b22465e65bf6" \\
  --scope "$(az keyvault show --name "${keyVaultName ?? '<vault-name>'}" --query id -o tsv)"`;

  return (
    <BottomDialog open={open} isFullScreen={true}>
      <PanelWrapper
        header={{
          title: 'Customer-managed Key Vault key',
          subtitle: <AzureKeyUriDescription />,
          fullWidthSubtitle: false,
          onClose,
        }}
        footer={{
          primaryButton: {
            text: 'Done',
            'data-testid': 'azure-key-uri-done-button',
            onClick: () => {
              setData(localData);
              onClose();
            },
          },
          secondaryButton: {
            text: 'Cancel',
            'data-testid': 'azure-key-uri-cancel-button',
            onClick: onClose,
          },
        }}
      >
        <Box padding='40px min(100px,6vw)' width='100%' height='auto'>
          <BrowserGrid columns={2} gap='40px'>
            <Stack
              gap='40px'
              divider={<Divider sx={{ borderStyle: 'dashed' }} />}
            >
              <FormControl>
                <FormLabel>Key Vault tenant</FormLabel>
                <Autocomplete
                  loading={loadingTenants}
                  value={cmkTenantId || null}
                  onChange={(_, value) => {
                    setLocalData({
                      ...localData,
                      vaultAttributes: {
                        ...localData.vaultAttributes,
                        azure: {
                          ...localData.vaultAttributes.azure,
                          cmkKeyVaultTenantId: value ?? '',
                          cmkAppRegistrationId: '',
                        },
                      },
                    });
                  }}
                  options={tenantOptions}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder='Select tenant'
                      label=''
                      slotProps={{ inputLabel: { shrink: true } }}
                    />
                  )}
                />
              </FormControl>
              <FormControl>
                <FormLabel>App for Key Vault access</FormLabel>
                <Autocomplete
                  loading={loadingApps}
                  value={selectedApp}
                  onChange={(_, app) => {
                    setLocalData({
                      ...localData,
                      vaultAttributes: {
                        ...localData.vaultAttributes,
                        azure: {
                          ...localData.vaultAttributes.azure,
                          cmkAppRegistrationId: app?.appRegistrationId ?? '',
                        },
                      },
                    });
                  }}
                  options={appOptions}
                  isOptionEqualToValue={(a, b) =>
                    a.appRegistrationId === b.appRegistrationId
                  }
                  getOptionLabel={(app) =>
                    `App ID: ${app.applicationClientId}${app.servicePrincipalId ? ` | SP ID: ${app.servicePrincipalId}` : ''}`
                  }
                  disabled={!cmkTenantId}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={
                        cmkTenantId
                          ? 'Select app'
                          : 'Select a Key Vault tenant first'
                      }
                      label=''
                      slotProps={{ inputLabel: { shrink: true } }}
                    />
                  )}
                />
              </FormControl>
              <FormTextField
                title='Key Vault key URI'
                placeholder={keyVaultKeyUriConfig.placeholder}
                value={localData.vaultAttributes.azure?.encryptionKey ?? ''}
                onChange={(event) => {
                  setLocalData({
                    ...localData,
                    vaultAttributes: {
                      ...localData.vaultAttributes,
                      azure: {
                        ...localData.vaultAttributes.azure,
                        encryptionKey: event.target.value,
                      },
                    },
                  });
                }}
                validation={keyVaultKeyUriConfig.validation}
              />
            </Stack>
            <Stack gap='12px'>
              <Typography variant='subtitle1'>
                Grant Key Vault access
              </Typography>
              <Typography variant='body2'>
                The selected app&apos;s service principal must have the{' '}
                <strong>Key Vault Crypto Service Encryption User</strong> role
                on the Key Vault. Run the following script in your environment:
              </Typography>
              <CopyableCode
                title='Bash'
                code={roleAssignmentScript}
                blur={!canShowRoleScript}
              />
            </Stack>
          </BrowserGrid>
        </Box>
      </PanelWrapper>
    </BottomDialog>
  );
};

export const AzureKeyVaultKeyUriTextField = ({
  backupVault,
  setBackupVault,
  vaultAccountId,
  defaultAzureTenantId,
}: {
  backupVault: BackupVault;
  setBackupVault: (data: BackupVault) => void;
  vaultAccountId: string;
  defaultAzureTenantId?: string;
}) => {
  const [openEditor, setOpenEditor] = useState(false);
  const [editorKey, setEditorKey] = useState(0);
  const encryptionKey = backupVault.vaultAttributes.azure?.encryptionKey;

  return (
    <>
      <ChangeableTextField
        value={encryptionKey}
        onClick={() => {
          setEditorKey((k) => k + 1);
          setOpenEditor(true);
        }}
        placeholder='No key URI set'
        noValueButtonText='Set key URI'
      />
      <AzureKeyVaultKeyUriEditorPanel
        key={editorKey}
        open={openEditor}
        onClose={() => setOpenEditor(false)}
        data={backupVault}
        setData={setBackupVault}
        vaultAccountId={vaultAccountId}
        defaultAzureTenantId={defaultAzureTenantId}
      />
    </>
  );
};
